import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";
import { ChevronsUpDown, FolderKanban, Layers, Plus } from "lucide-react";
import { swrFetcher, type ProjectInfo } from "@/lib/api";
import { ProjectOptionRow } from "@/components/ProjectOptionRow";
import { useProjectStore } from "@/store/project";
import { cn } from "@/lib/utils";

/**
 * Workspace switcher. Lists active projects (archived excluded) and an
 * "All projects" option (selectedProjectId = null). The selection is the
 * scope used across Studio (recent generations) and new generations.
 */
export function ProjectSelector() {
  const navigate = useNavigate();
  const { data: projects } = useSWR<ProjectInfo[]>(
    "/api/projects?include_archived=false",
    swrFetcher
  );
  const selectedProjectId = useProjectStore((s) => s.selectedProjectId);
  const setSelectedProject = useProjectStore((s) => s.setSelectedProject);

  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Reconcile the persisted scope against the loaded list: if the selected project
  // no longer exists (deleted, archived, or from a previous account), fall back to
  // "All projects" so Generate doesn't fire at a stale id (NotFoundError).
  useEffect(() => {
    if (!projects || !selectedProjectId) return;
    if (!projects.some((p) => p.id === selectedProjectId)) {
      setSelectedProject(null);
    }
  }, [projects, selectedProjectId, setSelectedProject]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const active = projects ?? [];
  const selected = selectedProjectId
    ? active.find((p) => p.id === selectedProjectId) ?? null
    : null;
  const label = selectedProjectId ? selected?.name ?? "Project" : "All projects";

  const choose = (id: string | null) => {
    setSelectedProject(id);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "flex min-h-11 w-full items-center gap-2 rounded-lg border border-studio-border bg-studio-card px-3 py-2 text-left text-sm transition-colors hover:border-studio-border-light",
          open && "border-studio-border-light"
        )}
      >
        {selectedProjectId ? (
          <FolderKanban className="h-4 w-4 shrink-0 text-studio-accent" />
        ) : (
          <Layers className="h-4 w-4 shrink-0 text-studio-muted" />
        )}
        <span className="min-w-0 flex-1 truncate text-studio-ink">{label}</span>
        <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-studio-faint" />
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1.5 overflow-hidden rounded-xl border border-studio-border-light bg-studio-panel shadow-panel animate-rise">
          <div className="max-h-72 overflow-y-auto p-1.5">
            <ProjectOptionRow
              icon={<Layers className="h-4 w-4 text-studio-muted" />}
              label="All projects"
              selected={!selectedProjectId}
              onClick={() => choose(null)}
            />
            {active.length > 0 && (
              <div className="my-1 border-t border-studio-border" />
            )}
            {active.map((p) => (
              <ProjectOptionRow
                key={p.id}
                icon={<FolderKanban className="h-4 w-4 text-studio-accent" />}
                label={p.name}
                selected={p.id === selectedProjectId}
                onClick={() => choose(p.id)}
              />
            ))}
          </div>
          <div className="border-t border-studio-border p-1.5">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate("/projects");
              }}
              className="flex min-h-11 w-full items-center gap-2 rounded-lg px-2.5 py-2 text-sm text-studio-muted transition-colors hover:bg-studio-card hover:text-studio-ink"
            >
              <Plus className="h-4 w-4" />
              New project
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
